import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { LoadAgentView } from '@1qa/shared';
import { CheckCircle2, Download, Gauge, Info, Pause, Play, Plus, RefreshCw, Trash2 } from '@/components/icons';
import { CopyField } from '@/components/CopyField';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { timeAgo } from '@/lib/format';
import { agentVersionName } from '@/lib/deviceAgentBuild';
import { LOAD_AGENT_DOWNLOAD, fetchLatestLoadAgent, loadAgentFileName, type LoadAgentBuild } from '@/lib/loadAgentBuild';
import { formatNumber } from '@/lib/perf';
import { Badge, Button, Card, Dialog, EmptyState, ErrorNotice, Field, Input, PageHeader, PageLoader, buttonClass, useConfirm, useToast } from '@/components/ui';

/** "4 cores · 8 GB · about 1,500 requests/s per core · 1 Gbit/s". */
function machineText(a: LoadAgentView): string {
  const m = a.machine;
  const parts = m ? [`${m.cores} cores`, `${formatNumber(m.memMb / 1024)} GB`, `${m.os}/${m.arch}`] : [];
  if (a.probe) parts.push(`measured: about ${formatNumber(a.probe.rpsPerCore)} requests/s per core, ${a.probe.memPerVuMb} MB per user`);
  else if (m) parts.push('not measured yet');
  parts.push(`${a.bandwidthMbit >= 1000 ? `${a.bandwidthMbit / 1000} Gbit/s` : `${a.bandwidthMbit} Mbit/s`} network`);
  if (a.filesFolder !== null) parts.push(a.filesFolder ? 'files folder set' : 'no files folder');
  return parts.join(' · ');
}

/**
 * Load agents: the machines performance tests run on (docs/design/load-agent.md). Anyone who starts load sees them;
 * Project Admins and Members connect their own. Each agent only calls out to 1QA.
 */
export function LoadAgentsPage() {
  const { currentOrgId, can, user } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const [connecting, setConnecting] = useState(false);
  const agents = useQuery({
    queryKey: ['load-agents', currentOrgId],
    queryFn: () => api.loadAgents(currentOrgId!),
    enabled: !!currentOrgId,
    refetchInterval: connecting ? 3000 : 10_000,
  });
  const latest = useQuery({ queryKey: ['load-agent-latest'], queryFn: fetchLatestLoadAgent, staleTime: 60_000 });
  const projects = useQuery({ queryKey: ['projects', currentOrgId], queryFn: () => api.projects(currentOrgId!), enabled: !!currentOrgId });
  const refresh = () => qc.invalidateQueries({ queryKey: ['load-agents', currentOrgId] });
  const update = useMutation({
    mutationFn: ({ a, paused }: { a: LoadAgentView; paused: boolean }) => api.updateLoadAgent(a.id, { paused }),
    onSuccess: (a) => {
      toast.success(a.paused ? `${a.name} is paused: no performance tests run there` : `${a.name} takes performance tests again`);
      refresh();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const probe = useMutation({
    mutationFn: (a: LoadAgentView) => api.probeLoadAgent(a.id),
    onSuccess: () => toast.info('The agent measures its machine now (about 40 seconds).'),
    onError: (e) => toast.error(errorMessage(e)),
  });
  const remove = useMutation({
    mutationFn: (a: LoadAgentView) => api.deleteLoadAgent(a.id),
    onSuccess: () => {
      toast.success('Load agent removed');
      refresh();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const canConnect = can('load.connect');
  const projectName = (id: string) => projects.data?.find((p) => p.id === id)?.name ?? 'a project';

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Performance"
        title="Load agents"
        description="The machines that put your app under load in performance tests. Run the 1QA Load Agent on a spare server or VM, ideally close to the app; it only connects out to 1QA."
        actions={
          <>
            <a href={LOAD_AGENT_DOWNLOAD} download={loadAgentFileName(latest.data ?? null)} className={buttonClass('outline', 'md')}>
              <Download className="size-3" /> Download agent
              {latest.data ? ` ${latest.data.versionName}` : ''}
            </a>
            {canConnect && (
              <Button onClick={() => setConnecting(true)} icon={<Plus className="size-4" />}>
                Connect a load agent
              </Button>
            )}
          </>
        }
      />
      {connecting && currentOrgId && (
        <ConnectDialog orgId={currentOrgId} agents={agents.data ?? []} projects={(projects.data ?? []).filter((p) => p.platform === 'PERFORMANCE')} build={latest.data ?? null} onClose={() => setConnecting(false)} />
      )}
      {agents.isPending ? (
        <PageLoader />
      ) : agents.isError ? (
        <ErrorNotice error={agents.error} onRetry={() => agents.refetch()} />
      ) : agents.data.length === 0 ? (
        <EmptyState
          icon={<Gauge className="size-6" />}
          title="No load agents yet"
          description="Performance tests need at least one load agent: a machine with Node.js 22 that runs the 1QA Load Agent. Setting one up takes about five minutes."
          action={
            canConnect && (
              <Button onClick={() => setConnecting(true)} icon={<Plus className="size-4" />}>
                Connect a load agent
              </Button>
            )
          }
        />
      ) : (
        <div className="space-y-4">
          {agents.data.map((a) => (
            <Card key={a.id} className="overflow-hidden" data-testid="load-agent">
              <div className="flex flex-wrap items-center gap-3 px-5 py-4">
                <span className="grid size-9 place-items-center rounded-card bg-primary-subtle text-primary">
                  <Gauge className="size-[18px]" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2 text-sm font-semibold text-fg">
                    {a.name}
                    {a.paused ? (
                      <Badge tone="warning">Paused</Badge>
                    ) : !a.online ? (
                      <Badge tone="muted">Offline</Badge>
                    ) : a.busyRunId ? (
                      <Badge tone="pending">Running a test</Badge>
                    ) : (
                      <Badge tone="success">Ready</Badge>
                    )}
                  </div>
                  <div className="truncate text-xs text-fg-secondary">
                    {a.hostname ? `${a.hostname} · ` : ''}
                    {a.online ? 'connected now' : a.lastSeenAt ? `last seen ${timeAgo(a.lastSeenAt)}` : 'never connected'}
                    {a.agentVersion && ` · agent ${agentVersionName(a.agentVersion)}`}
                    {a.engineVersion && ` · k6 ${a.engineVersion}`} · by {a.createdBy.id === user?.id ? 'you' : a.createdBy.name}
                  </div>
                </div>
                {a.busyRunId && (
                  <Link to={`/runs/${a.busyRunId}`} className="text-xs text-link hover:underline">
                    Open the run
                  </Link>
                )}
                {a.canManage && (
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" disabled={!a.online || !!a.busyRunId} loading={probe.isPending && probe.variables?.id === a.id} onClick={() => probe.mutate(a)} icon={<RefreshCw className="size-4" />}>
                      Measure again
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      loading={update.isPending && update.variables?.a.id === a.id}
                      onClick={() => update.mutate({ a, paused: !a.paused })}
                      icon={a.paused ? <Play className="size-4" /> : <Pause className="size-4" />}
                    >
                      {a.paused ? 'Resume' : 'Pause'}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label={`Remove ${a.name}`}
                      loading={remove.isPending && remove.variables?.id === a.id}
                      onClick={async () => {
                        const ok = await confirm({
                          title: `Remove ${a.name}?`,
                          message: 'Its token stops working at once; a test running there stops. You can connect the machine again later with a new token.',
                          confirmLabel: 'Remove',
                        });
                        if (ok) remove.mutate(a);
                      }}
                      icon={<Trash2 className="size-4" />}
                    />
                  </div>
                )}
              </div>
              <div className="space-y-1 border-t border-border-subtle px-5 py-3 text-xs text-fg-secondary">
                <p>{a.online || a.machine ? machineText(a) : `The agent is not running. On ${a.hostname ?? 'that machine'}, start it again with the command from when it was connected.`}</p>
                <p>{a.projectIds.length ? `Only for: ${a.projectIds.map(projectName).join(', ')}` : 'For every performance project of the organization'}</p>
              </div>
            </Card>
          ))}
          <p className="flex items-start gap-2 text-xs text-fg-muted">
            <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden />
            A busy machine gives slower numbers: if an agent works at its limit during a run, the report says the result can’t be trusted. Use more or bigger agents then.
          </p>
        </div>
      )}
    </div>
  );
}

function ConnectDialog({
  orgId,
  agents,
  projects,
  build,
  onClose,
}: {
  orgId: string;
  agents: LoadAgentView[];
  projects: { id: string; name: string }[];
  build: LoadAgentBuild | null;
  onClose: () => void;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [bandwidth, setBandwidth] = useState(1000);
  const [only, setOnly] = useState<string[]>([]);
  const [created, setCreated] = useState<{ agent: LoadAgentView; token: string } | null>(null);
  const create = useMutation({
    mutationFn: () => api.createLoadAgent(orgId, { name: name.trim(), bandwidthMbit: bandwidth, projectIds: only }),
    onSuccess: (res) => {
      setCreated(res);
      qc.invalidateQueries({ queryKey: ['load-agents', orgId] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const live = created && agents.find((a) => a.id === created.agent.id);
  const command = created ? `node 1qa-load-agent.mjs --server ${window.location.origin} --token ${created.token}` : '';

  if (!created) {
    return (
      <Dialog
        open
        onClose={onClose}
        title="Connect a load agent"
        description="Name the machine that will generate the load, so everyone knows where tests run."
        footer={
          <>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={() => create.mutate()} disabled={!name.trim() || bandwidth < 1} loading={create.isPending}>
              Continue
            </Button>
          </>
        }
      >
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim()) create.mutate();
          }}
        >
          <Field label="Name">{(id) => <Input id={id} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. perf-vm-1, Staging load box" maxLength={60} autoFocus />}</Field>
          <Field label="Network speed (Mbit/s)" hint="How fast the machine’s network is: 1000 for 1 Gbit/s. Used to estimate how many users it can carry.">
            {(id) => <Input id={id} type="number" min={1} max={100_000} value={bandwidth} onChange={(e) => setBandwidth(Number(e.target.value))} />}
          </Field>
          {projects.length > 1 && (
            <fieldset className="space-y-2">
              <legend className="text-xs font-medium tracking-label text-fg-secondary uppercase">Projects</legend>
              <p className="text-xs text-fg-muted">Leave all unticked to let every project use it.</p>
              <div className="grid max-h-40 gap-1 overflow-y-auto sm:grid-cols-2">
                {projects.map((p) => (
                  <label key={p.id} className="flex items-center gap-2 text-sm text-fg">
                    <input
                      type="checkbox"
                      className="size-4 accent-primary"
                      checked={only.includes(p.id)}
                      onChange={(e) => setOnly((o) => (e.target.checked ? [...o, p.id] : o.filter((x) => x !== p.id)))}
                    />
                    {p.name}
                  </label>
                ))}
              </div>
            </fieldset>
          )}
        </form>
      </Dialog>
    );
  }

  return (
    <Dialog open onClose={onClose} size="lg" title={`Set up ${created.agent.name}`} description="Do this on the machine that will generate the load. It needs Node.js 22 or newer; nothing else to install.">
      <ol className="ml-5 list-decimal space-y-4 text-sm text-fg-secondary marker:font-semibold marker:text-primary">
        <li>
          <a href={LOAD_AGENT_DOWNLOAD} download={loadAgentFileName(build)} className="inline-flex items-center gap-1.5 font-medium text-link hover:underline">
            <Download className="size-4" /> Download the 1QA Load Agent
            {build ? ` ${build.versionName}` : ''}
          </a>{' '}
          and unzip it on that machine.
        </li>
        <li>
          <span>In the <code>1qa-load-agent</code> folder, start it with this command (it contains the agent’s token: keep it to yourself):</span>
          <div className="mt-2">
            <CopyField label="Command" value={command} secret />
          </div>
          <p className="mt-1 text-xs text-fg-muted">The token is shown only now. Lost it? Remove this agent and connect the machine again.</p>
          <p className="mt-1 text-xs text-fg-muted">
            Scripts that upload files from this machine (many photos, files you keep out of 1QA): add <code>--files-dir &lt;folder&gt;</code> to the command. Scripts name files
            by their path inside that folder; nothing outside it is ever sent.
          </p>
        </li>
        <li>On first start the agent downloads its load engine from 1QA, checks it, and measures the machine for about 40 seconds.</li>
      </ol>
      <div className="mt-5 rounded-card bg-surface-sunken p-4 text-sm ring-1 ring-border ring-inset" role="status">
        {!live?.online ? (
          <span className="text-fg-secondary">Waiting for the agent to connect…</span>
        ) : !live.probe ? (
          <span className="text-fg">{live.name} is connected and measuring its machine…</span>
        ) : (
          <span className="flex items-center gap-2 text-fg">
            <CheckCircle2 className="size-4 text-success" /> {live.name} is ready: about {formatNumber(live.probe.rpsPerCore)} requests/s per core. Performance tests can use it now.
          </span>
        )}
      </div>
      <div className="mt-4 flex justify-end">
        <Button onClick={onClose}>Done</Button>
      </div>
    </Dialog>
  );
}
